import React, { useEffect, useState } from 'react';
import { walletService, stripeService } from '../services';
import Modal from './Modal';
import { MODAL_ERROR, MODAL_FIELD, MODAL_LABEL, MODAL_NOTE, MODAL_WELL, modalChoice } from './modalContext';
import Button from './Button';

// Eligibility codes the backend returns when the user can't cash out because
// of a missing/incomplete Stripe Connect Express account. We detect these by
// the blockedReason text since the API doesn't surface a structured code.
function needsConnectOnboarding(reason) {
  if (!reason) return false;
  const r = reason.toLowerCase();
  return r.includes('connect') || r.includes('payout setup') || r.includes('stripe');
}

function fmt(cents) {
  return `$${(Math.abs(cents) / 100).toFixed(2)}`;
}

/** The API's floor (`WithdrawRequest`'s range attribute) — checked here so the field can say it. */
export const MIN_CASH_OUT_CENTS = 1000;

/** "12.5" / "$12.50" → 1250; anything that isn't dollars and cents → NaN. */
export function toCents(text) {
  const t = String(text ?? '').replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{0,2})?$/.test(t)) return NaN;
  return Math.round(parseFloat(t) * 100);
}

/** What is wrong with an amount before the server is asked, or null. */
export function amountProblem(cents, balanceCents) {
  if (Number.isNaN(cents) || cents <= 0) return 'Enter an amount to cash out.';
  if (cents < MIN_CASH_OUT_CENTS) return `Minimum withdrawal is ${fmt(MIN_CASH_OUT_CENTS)}`;
  if (cents > balanceCents) return `You have ${fmt(balanceCents)} in your Wallet — that's the most you can cash out.`;
  return null;
}

/**
 * CashOutModal — THE cash-out flow (Oct 2026: the wallet is the only way money leaves,
 * for hosts and guests alike) with Carrie-voice copy, on the Portal modal (Oct 26 2026).
 *
 * Checks eligibility on mount. If not eligible, shows contextual blocked copy (Stripe
 * payout setup missing / a pending commitment). If eligible, shows the fee preview —
 * Stripe's payout charge at cost, no Portal fee — and a two-option CTA (keep in Wallet
 * vs cash out). The amount is the person's to choose (Oct 29 2026): it starts at the whole
 * balance, ALL puts it back, and whatever isn't cashed out stays in the Wallet — the
 * breakdown says how much. The preview re-prices each amount (debounced) and SEND only ever
 * sends the amount the breakdown on screen was priced for.
 * An error from the send itself lands as a red band ABOVE the buttons,
 * where it is read before the next tap, never as a line under them.
 *
 * Props:
 *   isOpen, onClose
 *   currentBalanceCents
 *   onConfirm(amountCents) — parent runs the actual withdrawal request. May return a
 *     promise resolving to {success, error} — if it returns {success: false, error}, the
 *     modal surfaces that error inline instead of closing.
 */
/** The one bank-timing phrase — the completion email says the same words
 *  (`WithdrawalService.BankTimingPhrase`). Change both or neither. */
export const BANK_TIMING = "Stripe's payout usually lands in your bank in 2 business days.";

export default function CashOutModal({ isOpen, onClose, currentBalanceCents = 0, onConfirm }) {
  const [eligibility, setEligibility] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [amountText, setAmountText] = useState('');
  const [previewError, setPreviewError] = useState(null);
  const [pricing, setPricing] = useState(false);

  const balance = Math.max(0, currentBalanceCents || 0);
  const amountCents = toCents(amountText);
  const problem = amountProblem(amountCents, balance);

  // Every opening starts at the whole balance — the common case is one tap.
  useEffect(() => {
    if (isOpen) setAmountText((balance / 100).toFixed(2));
  }, [isOpen, balance]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      const e = await walletService.getGuestCashOutEligibility();
      if (cancelled) return;
      if (!e.success) {
        setError(e.error || 'Unable to check eligibility');
        setLoading(false);
        return;
      }
      setEligibility(e.data);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  // Price the chosen amount. A bad amount is never sent; a failed preview (e.g. "Amount too
  // small to cover Stripe's payout fee") is said in words rather than leaving the modal blank.
  const eligible = !!eligibility?.isEligible;
  useEffect(() => {
    if (!isOpen || !eligible) return undefined;
    setPreviewError(null);
    if (problem) { setPreview(null); setPricing(false); return undefined; }
    let cancelled = false;
    setPricing(true);
    const t = setTimeout(async () => {
      const p = await walletService.previewGuestCashOut(amountCents);
      if (cancelled) return;
      if (p.success && p.data?.isEligible !== false) setPreview(p.data);
      else { setPreview(null); setPreviewError(p.data?.blockedReason || p.error || 'Cash-out is unavailable right now.'); }
      setPricing(false);
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [isOpen, eligible, amountCents, problem]);

  if (!isOpen) return null;

  const keepIt = 'Your balance stays in your Wallet. Spend it on any event — full value, no fees.';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cash out">
      <div className="space-y-4 font-mono text-sm text-black">
        {loading && (
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#6B7280]">Checking…</div>
        )}

        {!loading && eligibility && !eligibility.isEligible && needsConnectOnboarding(eligibility.blockedReason) && (
          <div className="space-y-4">
            <p className="leading-[1.65]">
              To cash out, connect a bank account or debit card via Stripe.
              Takes about two minutes — Stripe handles ID + bank verification.
              After that you can move any wallet balance to your account
              (earnings, transfers, top-ups — all of it).
            </p>
            {error && <div className={MODAL_ERROR} role="alert">{error}</div>}
            <Button
              fullWidth
              disabled={submitting}
              onClick={async () => {
                setSubmitting(true);
                setError(null);
                // Pass redirectTo=wallet so Stripe returns the user to the
                // wallet page after onboarding, not the settings page. The
                // wallet page detects ?stripe=return and reopens this modal
                // so the user can finish the cash-out without retracing steps.
                const r = await stripeService.startOnboarding('wallet');
                if (r.success && r.data?.onboardingUrl) {
                  window.location.href = r.data.onboardingUrl;
                } else {
                  setError(r.error || 'Could not start Stripe onboarding');
                  setSubmitting(false);
                }
              }}
            >
              {submitting ? 'Opening Stripe…' : 'Set up bank deposit →'}
            </Button>
            <p className={`${MODAL_NOTE} text-center`}>
              Or close this and spend your balance on an event — no fees, full value.
            </p>
          </div>
        )}

        {!loading && eligibility && !eligibility.isEligible && !needsConnectOnboarding(eligibility.blockedReason) && (
          <div className="space-y-3">
            <div className={MODAL_ERROR} role="alert">{eligibility.blockedReason}</div>
            <p className={MODAL_NOTE}>{keepIt}</p>
            <Button fullWidth variant="secondary" onClick={onClose}>Keep it in my Wallet</Button>
          </div>
        )}

        {/* The whole balance is under the floor: nothing to choose, so say why. */}
        {!loading && eligible && balance < MIN_CASH_OUT_CENTS && (
          <div className="space-y-3">
            <div className={MODAL_ERROR} role="alert">Minimum withdrawal is {fmt(MIN_CASH_OUT_CENTS)}</div>
            <p className={MODAL_NOTE}>{keepIt}</p>
            <Button fullWidth variant="secondary" onClick={onClose}>Keep it in my Wallet</Button>
          </div>
        )}

        {!loading && eligible && balance >= MIN_CASH_OUT_CENTS && (
          <div className="space-y-4">
            <p className="leading-[1.65]">
              Oh, you're leaving the party early? Here's the deal: pick how much goes to the bank
              account you connected to Stripe — the rest stays in your Wallet. Stripe's payout fee
              comes off the top — their charge, passed through at cost, nothing from us.{' '}
              {BANK_TIMING}
            </p>
            <p className="leading-[1.65] text-[#4B5563]">
              Alternatively — keep it in your Wallet and spend the full amount on your next event.
              No fees. Full value. Only if you like to party.
            </p>

            <div>
              <label htmlFor="cashout-amount" className={MODAL_LABEL}>Amount to cash out</label>
              <div className="flex items-center gap-2">
                <div className="relative min-w-0 flex-1">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 font-mono text-sm text-[#6B7280]">$</span>
                  <input
                    id="cashout-amount"
                    type="text"
                    inputMode="decimal"
                    autoComplete="off"
                    value={amountText}
                    disabled={submitting}
                    onChange={(e) => { setAmountText(e.target.value); setError(null); }}
                    className={`${MODAL_FIELD} pl-8`}
                    data-testid="cashout-amount"
                  />
                </div>
                <button
                  type="button"
                  disabled={submitting || amountCents === balance}
                  onClick={() => { setAmountText((balance / 100).toFixed(2)); setError(null); }}
                  className={`${modalChoice(amountCents === balance)} flex-shrink-0`}
                >
                  All {fmt(balance)}
                </button>
              </div>
            </div>

            {(problem || previewError) && amountText.trim() !== '' && (
              <div className={MODAL_ERROR} role="alert">{problem || previewError}</div>
            )}

            {preview && !problem && (
              <div className={`${MODAL_WELL} space-y-2 text-[12px] ${pricing ? 'opacity-60' : ''}`} data-testid="cashout-breakdown">
                <div className="flex justify-between gap-3">
                  <span className="text-[#6B7280]">Cashing out</span>
                  <span>{fmt(preview.amountCents)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-[#6B7280]">Stripe payout fee</span>
                  <span>-{fmt(preview.feeCents)}</span>
                </div>
                <div className="flex justify-between gap-3 border-t border-black/10 pt-2 font-bold">
                  <span>To your bank</span>
                  <span>{fmt(preview.netAmountCents)}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-[#6B7280]">Stays in your Wallet</span>
                  <span data-testid="cashout-remaining">{fmt(balance - preview.amountCents)}</span>
                </div>
              </div>
            )}

            {/* Submit-time error: the withdrawal itself refused. Read before the next tap. */}
            {error && <div className={MODAL_ERROR} role="alert">{error}</div>}

            <div className="grid gap-2 sm:grid-cols-2">
              <Button onClick={onClose} variant="secondary" disabled={submitting}>Keep it in my Wallet</Button>
              <Button
                // Only the amount the breakdown on screen was priced for is ever sent.
                disabled={submitting || pricing || !!problem || !preview || preview.amountCents !== amountCents}
                onClick={async () => {
                  setSubmitting(true);
                  setError(null);
                  try {
                    const result = await onConfirm?.(preview.amountCents);
                    if (result && result.success === false) {
                      setError(result.error || 'Cash-out failed');
                    }
                  } catch (err) {
                    setError(err?.message || 'Cash-out failed');
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {submitting ? 'Processing…' : preview && !problem ? `Send ${fmt(preview.netAmountCents)} to bank` : 'Send to bank'}
              </Button>
            </div>
          </div>
        )}

        {/* The eligibility check itself failed: explain why rather than showing an empty modal. */}
        {!loading && !eligibility && error && (
          <div className="space-y-3">
            <div className={MODAL_ERROR} role="alert">{error}</div>
            <p className={MODAL_NOTE}>{keepIt}</p>
            <Button fullWidth variant="secondary" onClick={onClose}>Keep it in my Wallet</Button>
          </div>
        )}
      </div>
    </Modal>
  );
}
